import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { getContactsEfficiency, getProjects } from '../api';
import { ProjectAutocomplete } from '../components/ProjectAutocomplete';
import TablePagination from '@mui/material/TablePagination';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';

interface EfficiencyRow {
  contact: string;
  project_name: string;
  total_leads: number;
  hot_leads: number;
  hot_ratio: number;
  w1_ratio?: number;
  w2_ratio?: number;
  w3_ratio?: number;
  w4_ratio?: number;
}

export default function ContactsEfficiencyPage() {
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [projectId, setProjectId] = useState<string>('');
  const [contactSearch, setContactSearch] = useState<string>('');
  const [projects, setProjects] = useState<Array<{ id: number; name: string }>>([]);
  const [rows, setRows] = useState<EfficiencyRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [sortBy, setSortBy] = useState<keyof EfficiencyRow>('hot_ratio');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [lastCopiedContact, setLastCopiedContact] = useState<string | null>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    (async () => {
      try {
        const list = await getProjects();
        setProjects(list);
      } catch (e: any) {
        console.error(e);
      }
    })();
  }, []);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getContactsEfficiency({ dateFrom, dateTo, projectId: projectId || undefined });
      const data = res?.data || res; // backend returns { success, data } or array
      setRows(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (contact: string) => {
    try {
      await navigator.clipboard.writeText(contact);
      setLastCopiedContact(contact);
    } catch (_err) {
      try {
        const textArea = document.createElement('textarea');
        textArea.value = contact;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setLastCopiedContact(contact);
      } catch (_) {
        // ignore
      }
    }
  };

  const downloadCSV = (data: EfficiencyRow[], filename: string) => {
    const headers = [
      'Контакт',
      'Всего лидов',
      'Горячих лидов',
      'Доля горячих, %',
      '1w ago, %',
      '2w ago, %',
      '3w ago, %',
      '4w ago, %',
      'Проект',
    ];
    const formatRatioForCSV = (value: number | undefined) => {
      if (typeof value !== 'number') return '';
      const sign = value > 0 ? '+' : '';
      return `${sign}${value.toFixed(1)}%`;
    };
    const rowsCsv = data.map(r => [
      r.contact,
      r.total_leads,
      r.hot_leads,
      r.hot_ratio,
      formatRatioForCSV(r.w1_ratio),
      formatRatioForCSV(r.w2_ratio),
      formatRatioForCSV(r.w3_ratio),
      formatRatioForCSV(r.w4_ratio),
      r.project_name,
    ].join(','));
    const csv = [headers.join(','), ...rowsCsv].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportCurrentFiltersToExcel = () => {
    // экспортируем все отсортированные строки (не только текущую страницу)
    downloadCSV(sorted, `contacts_efficiency_${projectId || 'all'}_${new Date().toISOString().slice(0,10)}.csv`);
  };

  useEffect(() => {
    // initial load with defaults
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const q = contactSearch.trim().toLowerCase();
    return rows.filter(r => (q ? (r.contact?.toLowerCase().includes(q)) : true));
  }, [rows, contactSearch]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const av = (a[sortBy] ?? '') as any;
      const bv = (b[sortBy] ?? '') as any;
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortOrder === 'asc' ? av - bv : bv - av;
      }
      const as = String(av).toLowerCase();
      const bs = String(bv).toLowerCase();
      if (as < bs) return sortOrder === 'asc' ? -1 : 1;
      if (as > bs) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sortBy, sortOrder]);

  // Reset page on data/sort/filter change
  useEffect(() => {
    setPage(0);
  }, [contactSearch, dateFrom, dateTo, projectId, sortBy, sortOrder, rows.length]);

  const paged = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return sorted.slice(start, end);
  }, [sorted, page, rowsPerPage]);

  const total = useMemo(() => sorted.reduce((acc, r) => acc + r.total_leads, 0), [sorted]);
  const totalHot = useMemo(() => sorted.reduce((acc, r) => acc + r.hot_leads, 0), [sorted]);
  const totalRatio = useMemo(() => (total ? Math.round((totalHot / total) * 10000) / 100 : 0), [total, totalHot]);

  const headerCell = (label: string, key: keyof EfficiencyRow, align?: 'right') => (
    <TableCell
      align={align}
      onClick={() => {
        if (sortBy === key) {
          setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
          setSortBy(key);
          setSortOrder('desc');
        }
      }}
      sx={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
    >
      {label}{sortBy === key ? (sortOrder === 'asc' ? ' ▲' : ' ▼') : ''}
    </TableCell>
  );

  // Функция форматирования недельных коэффициентов с цветовым кодированием
  const formatWeeklyRatio = (value: number | undefined) => {
    if (typeof value !== 'number') return '';
    const sign = value > 0 ? '+' : '';
    const color = value > 0 ? '#2e7d32' : value < 0 ? '#d32f2f' : '#666';
    return (
      <span style={{ color, fontWeight: value !== 0 ? 600 : 400 }}>
        {sign}{value.toFixed(1)}%
      </span>
    );
  };

  

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Typography variant={isMobile ? "h5" : "h4"} sx={{ mb: 2 }}>Эффективность контактов</Typography>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, flexDirection: isMobile ? 'column' : 'row' }}>
        <Box sx={{ display: 'flex', gap: 2, width: isMobile ? '100%' : 'auto' }}>
          <TextField
            label="Дата от"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ flex: 1 }}
          />
          <TextField
            label="Дата до"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ flex: 1 }}
          />
        </Box>
        <ProjectAutocomplete
          projects={projects}
          value={projectId}
          onChange={(val) => setProjectId(val)}
          sx={{ minWidth: isMobile ? '100%' : 240 }}
          size="small"
        />
        <TextField
          label="Поиск по контакту"
          value={contactSearch}
          onChange={(e) => setContactSearch(e.target.value)}
          size="small"
          fullWidth={isMobile}
          sx={{ minWidth: isMobile ? '100%' : 240 }}
        />
        <Stack direction="row" spacing={2} sx={{ width: isMobile ? '100%' : 'auto' }}>
          <Button variant="contained" onClick={load} disabled={loading} sx={{ flex: 1 }}>Применить</Button>
          <Button variant="outlined" onClick={exportCurrentFiltersToExcel} disabled={loading} sx={{ flex: 1 }}>Excel</Button>
        </Stack>
      </Box>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
      )}

      <Paper sx={{ overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: '70vh' }}>
          <Table size={isMobile ? "small" : "medium"} stickyHeader>
            <TableHead>
              <TableRow>
                {headerCell('Контакт', 'contact')}
                {headerCell('Всего', 'total_leads', 'right')}
                {!isMobile && headerCell('Горячих', 'hot_leads', 'right')}
                {headerCell('Доля, %', 'hot_ratio', 'right')}
                {!isMobile && headerCell('1w ago, %', 'w1_ratio', 'right')}
                {!isMobile && headerCell('2w ago, %', 'w2_ratio', 'right')}
                {headerCell('Проект', 'project_name')}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={isMobile ? 5 : 10} align="center"><CircularProgress size={20} /></TableCell>
                </TableRow>
              ) : sorted.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isMobile ? 5 : 10} align="center">Нет данных</TableCell>
                </TableRow>
              ) : (
                paged.map((r) => (
                  <TableRow key={`${r.contact}-${r.project_name}`} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ flex: 1, fontSize: isMobile ? '0.75rem' : '0.875rem', wordBreak: 'break-all' }}>
                          {r.contact}
                        </Typography>
                        <Tooltip title={lastCopiedContact === r.contact ? "Скопировано!" : "Копировать контакт"}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              copyToClipboard(r.contact);
                            }}
                            sx={{ 
                              color: lastCopiedContact === r.contact ? 'success.main' : 'action.active',
                              p: 0.5
                            }}
                          >
                            {lastCopiedContact === r.contact ? <CheckIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>{r.total_leads}</TableCell>
                    {!isMobile && <TableCell align="right">{r.hot_leads}</TableCell>}
                    <TableCell align="right" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', fontWeight: 'bold' }}>{r.hot_ratio}%</TableCell>
                    {!isMobile && <TableCell align="right">{formatWeeklyRatio(r.w1_ratio)}</TableCell>}
                    {!isMobile && <TableCell align="right">{formatWeeklyRatio(r.w2_ratio)}</TableCell>}
                    <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>{r.project_name}</TableCell>
                  </TableRow>
                ))
              )}
              {sorted.length > 0 && !loading && (
                <TableRow sx={{ bgcolor: '#f9f9f9' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Итого</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{total}</TableCell>
                  {!isMobile && <TableCell align="right" sx={{ fontWeight: 'bold' }}>{totalHot}</TableCell>}
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{totalRatio}%</TableCell>
                  {!isMobile && <TableCell colSpan={2} />}
                  <TableCell />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          rowsPerPageOptions={[10, 25, 50, 100]}
          count={sorted.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            const next = parseInt(e.target.value, 10);
            setRowsPerPage(next);
            setPage(0);
          }}
          labelRowsPerPage={isMobile ? "Стр:" : "Строк на странице:"}
        />
      </Paper>
    </Box>
  );
} 